import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  AfterViewInit,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgClass, NgStyle } from '@angular/common';
import {
  FallbackColumnInterface,
  HeaderTableColumnInterface,
  PaginationInterface,
  ResizeMoveTableInterface,
  SortChangedInterface,
  SortOrder,
  TranslocoFunction,
} from '../../types';
import { FormPaginationComponent } from '../../form';
import { SortableHeaderComponent } from '../sortable-header/sortable-header.component';
import { ResizeHandlerComponent } from '../resize-handler/resize-handler.component';
import { TableWidthPersistenceService } from '../../services';
import { TableEllipsisDirective } from '@core-utils';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { provideUiKitScope } from '../../providers';

const MIN_COLUMN_WIDTH = 30;
const MAX_COLUMN_WIDTH = 2000000;

@Component({
  selector: 'ui-table',
  imports: [
    FormPaginationComponent,
    SortableHeaderComponent,
    NgClass,
    ResizeHandlerComponent,
    NgStyle,
    TableEllipsisDirective,
    CdkDropList,
    CdkDrag,
    TranslocoDirective,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TableWidthPersistenceService, provideUiKitScope()],
})
export class TableComponent implements AfterViewInit {
  private widthPersistence = inject(TableWidthPersistenceService);

  tableId = input.required<string>();
  columns = model.required<HeaderTableColumnInterface[]>();
  t = input.required<TranslocoFunction>();
  isLoading = input<boolean>(false);

  collectionSize = input.required<number>();
  isDraggable = input<boolean>(false);
  page = input.required<number>();
  pageSize = input.required<number>();
  sortableField = input<SortChangedInterface | null>(null);

  paginationValue = output<PaginationInterface>();
  sortableValue = output<SortChangedInterface>();

  tableRef = viewChild<ElementRef<HTMLTableElement>>('table');

  sortField: string | null = '';
  sortOrder: SortOrder = 'ASC';

  columnWidths = signal<Map<string, number>>(new Map());
  isResizing = signal(false);

  columnStyles = computed(() => {
    const styles: Record<number, Partial<CSSStyleDeclaration>> = {};
    const widths = this.columnWidths();

    this.columns().forEach((col, index) => {
      const width = widths.get(col.field);
      styles[index] = {
        width: col.width || (width ? `${width}px` : 'auto'),
        minWidth: col.minWidth ?? `${MIN_COLUMN_WIDTH}px`,
        maxWidth: col.maxWidth ?? 'none',
      };
    });

    return styles;
  });

  constructor() {
    effect(() => {
      const sort = this.sortableField();
      this.sortField = sort?.sortField || '';
      this.sortOrder = sort?.sortOrder || 'ASC';

      const cols = this.columns();

      if (!this.columnWidths().size) {
        const defaultWidths = new Map<string, number>();
        cols.forEach((col) => {
          const initial = this.resolveInitialWidth(col);
          if (initial !== null) defaultWidths.set(col.field, initial);
        });
        this.columnWidths.set(defaultWidths);
      }
    });

    afterNextRender(() => {
      this.restoreColumnWidths();
    });
  }

  ngAfterViewInit() {
    const tableEl = this.tableRef()?.nativeElement;
    if (!tableEl) return;

    const observer = new MutationObserver(() => {
      const saved = this.widthPersistence.load(this.tableId());
      if (saved?.order) {
        this.reorderBodyRows(saved.order);
      }
    });

    observer.observe(tableEl.querySelector('tbody') as Node, {
      childList: true,
    });
  }

  drop(event: CdkDragDrop<HeaderTableColumnInterface[]>) {
    const columns = [...this.columns()];
    moveItemInArray(columns, event.previousIndex, event.currentIndex);

    columns.forEach((file, index) => {
      file.order = index;
    });

    this.columns.set(columns);
    this.reorderTableRows(event.previousIndex, event.currentIndex);
    this.persistColumnWidths();
  }

  private reorderTableRows(prevIndex: number, currIndex: number) {
    const tableEl = this.tableRef()?.nativeElement;
    if (!tableEl) return;

    const rows = Array.from(tableEl.querySelectorAll('tbody tr'));
    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells[prevIndex]) {
        const movedCell = cells[prevIndex];
        if (currIndex >= cells.length - 1) {
          row.appendChild(movedCell);
        } else if (currIndex > prevIndex) {
          row.insertBefore(movedCell, cells[currIndex].nextSibling);
        } else {
          row.insertBefore(movedCell, cells[currIndex]);
        }
      }
    });
  }

  getColumnStyle(index: number) {
    return this.columnStyles()[index] ?? {};
  }

  onSortChanged(event: SortChangedInterface) {
    this.sortField = event.sortField;
    this.sortableValue.emit(event);
  }

  onResizeStart() {
    this.isResizing.set(true);
  }

  onResizeMove(event: ResizeMoveTableInterface) {
    const newWidths = this.calculateNewWidths(event);
    this.columnWidths.set(newWidths);
  }

  onResizeEnd() {
    this.isResizing.set(false);
    this.persistColumnWidths();
  }

  private resolveInitialWidth(col: HeaderTableColumnInterface): number | null {
    if (col.initialWidth?.endsWith('px')) {
      return this.parseWidth(col.initialWidth);
    }
    if (col.minWidth?.endsWith('px')) {
      return this.parseWidth(col.minWidth);
    }
    return null;
  }

  private parseWidth(width: string): number {
    return width.endsWith('px') ? parseInt(width, 10) : MIN_COLUMN_WIDTH;
  }

  private calculateNewWidths(
    data: ResizeMoveTableInterface,
  ): Map<string, number> {
    const widths = new Map(this.columnWidths());
    const column = this.columns()[data.columnIndex];
    const key = column.field;

    const currentWidth =
      widths.get(key) ??
      this.getFallbackColumnWidth({
        index: data.columnIndex,
        column,
      });

    let newWidth = currentWidth + data.deltaX;

    const minWidth = column.minWidth
      ? this.parseWidth(column.minWidth)
      : MIN_COLUMN_WIDTH;
    newWidth = Math.max(newWidth, minWidth);

    if (column.maxWidth) {
      const maxWidth = this.parseWidth(column.maxWidth);
      newWidth = Math.min(newWidth, maxWidth);
    }

    newWidth = Math.max(newWidth, MIN_COLUMN_WIDTH);
    newWidth = Math.min(newWidth, MAX_COLUMN_WIDTH);

    widths.set(key, newWidth);
    return widths;
  }

  private getFallbackColumnWidth(data: FallbackColumnInterface): number {
    const tableEl = this.tableRef()?.nativeElement;
    const headerCell = tableEl?.querySelectorAll('th')?.[data.index];
    if (headerCell) return headerCell.offsetWidth;
    return data.column.minWidth
      ? this.parseWidth(data.column.minWidth)
      : MIN_COLUMN_WIDTH;
  }

  private persistColumnWidths() {
    const order = this.columns().map((col) => col.field);
    this.widthPersistence.save(this.tableId(), this.columnWidths(), order);
  }

  private restoreColumnWidths() {
    const saved = this.widthPersistence.load(this.tableId());

    if (saved) {
      const mapWidths = new Map<string, number>(saved.widths);
      this.columnWidths.set(mapWidths);

      if (saved.order.length) {
        const current = [...this.columns()];
        const sorted = saved.order
          .map((f) => current.find((c) => c.field === f))
          .filter(Boolean) as HeaderTableColumnInterface[];

        const extras = current.filter((c) => !saved.order.includes(c.field));
        this.columns.set([...sorted, ...extras]);
      }
    }
  }

  private reorderBodyRows(order: string[]) {
    const tableEl = this.tableRef()?.nativeElement;
    if (!tableEl) return;

    const rows = Array.from(tableEl.querySelectorAll('tbody tr'));

    rows.forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      const reordered = order
        .map((field) =>
          cells.find((cell) => cell.getAttribute('data-field') === field),
        )
        .filter(Boolean);

      reordered.forEach((cell) =>
        row.appendChild(cell as HTMLTableCellElement),
      );
    });
  }
}
